import {VNode} from 'snabbdom';

import type {ContextPanelData} from 'common/widgets/context-panel/types';
import {Placement, TooltipAttrs} from 'common/components/tooltip/types';
import type {ModifierKeys} from 'common/util/keyboard';

import type {SelectionState} from './constants';

/**
 * NOTE: Please keep the properties in items menu types generic enough such that they could be used for any list.
 * Not just events or properties. Being generic means we could re-use it in more places and be consistent.
 * We should aim for items-menu as the defacto item list renderer at mixpanel.
 */

export type SectionsFromSearchInputFn = (input: string, currentSections?: Section[]) => Section[];

export type SectionItemContextPanelTransform =
  | ((item: SectionItem) => ContextPanelData)
  | ((item: SectionItem, section: Section) => ContextPanelData);

export type SearchMatchResult = {score: number; matchParts: string[]; matchStr: string};
export type CustomSearchMatcherFn = (item: SectionItem, searchString: string) => SearchMatchResult;
export enum LabelTheme {
  Strong = `strong`,
  Soft = `soft`,
  Italic = `italic`,
  InvertedCheckmark = `inverted-checkmark`,
}
export enum SubLabelTheme {
  Breadcrumb = `breadcrumb`,
  Soft = `soft`,
  Strong = `strong`,
}
export enum SelectAllMechanism {
  Full = `full`,
  Partial = `partial`,
}

/**
 * Too many existing instances of mp-select and mp-items-menu shove additional properties inside SectionItem.
 * To handle these cases we include {[key: string]: any} to allow any extra keys.
 * ExtendWithT has been implemented to slowly correct this overly-loose definition.
 * While abnormal, the approach allows the caller to override the any index signature, therefore making it strict.
 * Ex:
 * MyValueType = {value: string}
 * MyStrictSectionItem = SectionItem<MyValueType, {moreData: string}>;
 * MyStrictSection = Section<MyValueType, SectionItem>;
 *
 * If you do not need to extend SectionItem but still want strict typing use NoExtension;
 * PlainStrictSectionItem = SectionItem<MyValueType, NoExtension>;
 */

// We disable non-nullish values for typing safety.
// However, In this instance we want to use it to signify that there is no values to extends.
// We declare the type here to prevent the need for lint-disables for it's users.
// eslint-disable-next-line @typescript-eslint/ban-types
export type NoExtension = {};

export type Section<T = any, ExtendWithT = Obj, SectionItemT = SectionItem<T, ExtendWithT>> = {
  /** Section's label. If not present, only a horizontal separator rule is shown */
  label?: string;

  /** svg-icon shown to the right of the section label */
  icon?: string;

  /** Tooltip shown on icon hover for additional information */
  tooltip?: TooltipInfo;

  /** The items inside the section */
  items?: SectionItemT[];

  /** A spinny is shown inside the section if isLoading is true */
  isLoading?: boolean;

  /** Number of items to show per section. If more items than showLimit a 'Show All' expander is shown */
  showLimit?: number;

  /** if we want to display all selections, but only want to show the ability to select one */
  displayAllSelectOne?: boolean;

  /** Allows a section to be included in all logic but not rendered in the menu.
      This is useful for conditionally showing after a response from the menuiitem */
  isHidden?: boolean;

  /** Customize the icon of the item. Only available if items are passed via props */
  iconRenderer?: (item: SectionItem) => VNode;

  /** Allows to add custom actions at the right of the item. Only available if items are passed via props */
  actionsRenderer?: (item: SectionItem) => VNode;

  /** Customize the icon next to the section label. Only available if items are passed via props */
  sectionLabelIconRenderer?: (item: Section) => VNode;

  /** Hide the top section divider */
  hideTopDivider?: boolean;
};

export type SectionItem<ValueT = any, ExtendWithT = Obj> = {
  /** Item's label. This should always be present */
  label: string;

  /** A shorter version of label, used in menu summaries */
  shortLabel?: string;

  /** label is truncated by default. wrapLabel: true turns on wrapping so user can read full label */
  wrapLabel?: boolean;

  /** A short label shown on the right side (e.g. for keyboard shortcuts) */
  sideLabel?: string;

  /** A short blurb / description about item that is shown on another line */
  subLabel?: string;

  /** subLabel is truncated by default. wrapSubLabel: true turns on wrapping so user can read full description */
  wrapSubLabel?: boolean;

  /** Theme styling for the label. */
  labelTheme?: LabelTheme;

  /** Theme styling for the sublabel. */
  subLabelTheme?: SubLabelTheme;

  /** svg-icon shown besides the label */
  icon?: string;

  /** is the icon highlighted  */
  highlightIcon?: boolean;

  /** By default icon is shown on left, but user can specify right icon e.g BETA icon */
  iconSide?: `left` | `right`;

  /** Can the item be selected. Item is greyed out if disabled */
  isDisabled?: boolean;

  /** Marks the item as selected */
  isSelected?: boolean;

  /** Should '>' caret be shown besides the item. Used to indicate sub items */
  hasCaret?: boolean;

  /** Should '>' caret be shown even when item is not hovered/focused */
  persistCaret?: boolean;

  /** By default, use the caret icon ()>); can specify `arrow` to use the arrow icon (->) */
  caretStyle?: `caret` | `arrow`;

  /** Wrap the item in an anchor tag with a link */
  href?: string;

  /** anchor tag target */
  target?: string;

  /** item is not clickable */
  isReadOnly?: boolean;

  /**
   * Also used to indicate sub items, but either item or sub items can be selected.
   * We should re-think this with design. Not the most intuitive UX experience
   */
  hasPill?: boolean;

  /** Label of the pill e.g. 'Properties' or 'Typecast' */
  pillLabel?: string;

  /** Tooltip shown on item hover for additional information */
  tooltip?: TooltipInfo;

  /** Whether the pill is disabled. */
  isPillDisabled?: boolean;

  /** Hides selectable controls and indicates that this item is not a list item */
  isControlItem?: boolean;

  /** Optional name for the selected value. Equivalent to "value" if not present.
   * Example: "Move to dashboard 333" would be {name: `move`, value: 333}
   **/
  name?: string;

  /** Optional value included in select event */
  value?: ValueT;

  /** Color of the checkbox if it's a checkbox */
  color?: string;

  /** Defines nested sections */
  subSections?: Array<Section<ValueT, ExtendWithT, SectionItem<ValueT>>>;

  /** Disable flipping on sub menu */
  subDisableFlip?: boolean;

  /** Defines any referenced named slots */
  slot?: string;

  /** Tags whether or not the event was generated by user */
  itemFromInput?: boolean;

  /** Customize the icon of the item. Only available if items are passed via props */
  iconRenderer?: (item: SectionItem) => VNode;

  /** Allows to add custom actions at the right of the item. Only available if items are passed via props */
  actionsRenderer?: (item: SectionItem) => VNode;

  /** Render a custom sub-menu when the item is focused. Only available if items are passed via props **/
  subMenuRenderer?: (onValueSelected: (value: string | number, additionalInfo: Obj) => void) => VNode;

  /** Do not consider item when checking for matches against the input **/
  excludeFromInputMatch?: boolean;

  /** Specifies if this property item is in the recently used list */
  isRecentlyUsed?: boolean;

  highlightItem?: boolean;
} & ExtendWithT;

export interface TooltipInfo extends Omit<TooltipAttrs, 'placement'> {
  /** Placement of the tooltip within the section, only allow left or right */
  placement?: Placement;
}

export interface WrappedSectionItem<T = any> {
  index?: number;
  labelMatch?: SearchMatchResult | null;
  subLabelMatch?: SearchMatchResult | null;
  customMatch?: SearchMatchResult | null;
  wrapped: SectionItem<T>;
}

export interface WrappedSection<T = any> extends Section<T, Obj, WrappedSectionItem> {
  wrappedSectionIndex?: number;
  items: WrappedSectionItem[];
}

export type WrappedSectionDetails<T = any> = Omit<WrappedSection<T>, 'wrappedSectionIndex' | 'items'>;

export interface ItemsMenuState {
  customSearchMatcherFn: CustomSearchMatcherFn;
  fullyShownSections: Obj<boolean>; // Map to track which sections have 'show all' clicked
  isPillFocused: boolean;
  openedSubMenuIndex: number;
  keyedItems: {[labelKey: string]: SectionItem};
  progressiveListSize: number;
  sectionsFromInputFn: SectionsFromSearchInputFn;
  sections: Section[];
  selectionState: SelectionState;
  tooltipData: ContextPanelData;
  focusedItemIndex: number;
  focusedItem: {
    sectionDetails: WrappedSectionDetails | null;
    wrappedItem: WrappedSectionItem | null;
  };
}

export interface ElementRefs {
  containerEl?: HTMLElement;
}

export enum ItemsMenuTheme {
  Default = `default`,
  New = `new`,
  People = `people`,
}

export interface ItemsMenuAttrs {
  'align-all-text'?: boolean;
  'disable-first-item-padding'?: boolean;
  'disable-search-filter-matching-parts'?: boolean;
  'disable-search-sort'?: boolean;
  'empty-message'?: string;
  'empty-search-results-message'?: string;
  'experimental-positioning'?: boolean;
  multiple?: boolean;
  'project-is-free-plan'?: boolean;
  'search-exact'?: boolean;
  'search-filter'?: string;
  'sections-hint'?: string;
  sections?: Section[];
  'select-all-disabled'?: boolean;
  'select-all-matching-disabled'?: boolean;
  'select-all-mechanism'?: SelectAllMechanism;
  'select-all-tooltip'?: TooltipInfo;
  'show-csv-matches'?: boolean;
  'show-select-all-matching'?: boolean;
  'show-select-all'?: boolean;
  'start-focus-on-selected'?: boolean;
  theme?: ItemsMenuTheme;
  'unfocus-on-mouseleave'?: boolean;
}

export interface ItemsMenuJsxProps {
  attrs?: ItemsMenuAttrs;
  props?: {
    sectionsFromInputFn?: SectionsFromSearchInputFn;
    contextPanelTransform?: SectionItemContextPanelTransform;
    customSearchMatcherFn?: CustomSearchMatcherFn;
    sections?: Section[];
    keydownProxyEl?: HTMLElement;
    contextPanelPositionParentElement?: HTMLElement;
  };
  on?: {
    navigateBack?: (ev?: CustomEvent) => void;
    selectAll?: (ev?: CustomEvent) => void;
    selectAllMatching?: (ev?: CustomEvent) => void;
    clickItem?: (ev?: ItemClickEvent) => void;
    clickExpanderItem?: (ev?: ExpanderItemClickEvent) => void;
    clickPill?: (ev?: CustomEvent) => void;
    focusItem?: (ev?: ItemFocusEvent) => void;
    draw?: (ev?: CustomEvent) => void;
    change?: (ev?: CustomEvent) => void;
  };
}

export type EventHandler = (ev: CustomEvent) => void;
export type ItemHandler = (item: WrappedSectionItem) => void;
export type ItemAndEventHandler = (item: WrappedSectionItem, ev: Event) => void;
export type HTMLElementHandler = (el: HTMLElement) => void;

export type ExpanderItemClickEvent<T = any> = CustomEvent<{
  section: Section<T>;
  sectionIdx: number;
}>;

export type ItemFocusEvent<T = any, SectionT = Section<T>, SectionItemT = SectionItem<T>> = CustomEvent<{
  item: SectionItemT;
  section: SectionT;
}>;

export type ItemClickEvent<T = any, SectionT = Section<T>, SectionItemT = SectionItem<T>> = CustomEvent<
  SectionItem<T> & {
    topScrollPosition: number;
    selectionState: SelectionState;
    modifierKeys: ModifierKeys;
    item: SectionItemT;
    section: SectionT;
  }
>;
